/**
 * Aptean brand assets — sourced from aptean-logo-yellow-white.svg
 *
 * The SVG uses two fill colours:
 *   #fff     — "aptean" wordmark letters (white)
 *   #f8ed44  — icon mark (yellow)
 *
 * Because white text is invisible on light backgrounds, components that
 * render on a light sidebar background wrap the SVG in a dark container.
 */

interface ApteanLogoProps {
  size?: number
  className?: string
}

/* ─── Icon mark only (yellow geometric shape, square crop) ──────────────── */
export function ApteanLogo({ size = 32, className }: ApteanLogoProps) {
  return (
    <svg
      width={size}
      height={size}
      /* crop to the icon-mark portion of the full 1300×343 viewBox */
      viewBox="0 0 315 343"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Aptean"
    >
      {/* Dark navy background square */}
      <rect width="315" height="343" rx="9" fill="#0E1724" />

      {/* ── Yellow icon mark paths ── */}
      <path
        fill="#f8ed44"
        d="M619.32,594.89,484.58,374.21a11.86,11.86,0,0,0-14.08-5L608.79,595.68a12.38,12.38,0,0,1,.57,11.86L582.07,663.9a12.45,12.45,0,0,1-1.94,2.9,11.86,11.86,0,0,0,13.52-6.37l26.22-54.14A11.91,11.91,0,0,0,619.32,594.89Z"
        transform="translate(-309.99 -368.51)"
      />
      <polygon fill="#f8ed44" points="189.82 265.75 209.48 259.48 225 285.07 189.82 265.75" />
      <polygon fill="#f8ed44" points="98.03 81.03 145.87 158.31 133.48 186.02 35.95 215.78 98.03 81.03" />
      <path
        fill="#f8ed44"
        d="M311,660.11l30.17-65.48,132.21-40.39,41.3,66.37L323.73,675A10.76,10.76,0,0,1,311,660.11Z"
        transform="translate(-309.99 -368.51)"
      />
      <path
        fill="#f8ed44"
        d="M598.37,607.58l-26.32,54.33a12,12,0,0,1-20.9,1.13L412.06,440.77l29.42-64.85a12,12,0,0,1,21.09-1.29l135.24,221.5A12,12,0,0,1,598.37,607.58Z"
        transform="translate(-309.99 -368.51)"
      />
    </svg>
  )
}

/* ─── Full wordmark (icon + "aptean" letters) on dark pill ──────────────── */
export function ApteanWordmark() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      {/* Dark container so the white+yellow SVG is visible on light sidebar bg */}
      <div style={{
        background: '#0E1724',
        borderRadius: '7px',
        padding: '6px 12px',
        display: 'inline-flex',
        alignItems: 'center',
      }}>
        <svg
          /* height fixed; width derived from aspect ratio 1300:343 ≈ 3.79 */
          height={26}
          width={Math.round(26 * (1300.01 / 342.99))}
          viewBox="0 0 1300.01 342.99"
          xmlns="http://www.w3.org/2000/svg"
          aria-label="Aptean"
        >
          {/* ── White wordmark letters (a p t e a n) ── */}
          <path fill="#ffffff" d="M720.64,644.3c-19.66,0-34.79-5.16-45-15.34-9.17-9.51-14.25-23.44-14.25-39.19,0-31.43,22.68-50.19,60.66-50.19h48.07V525.83c0-23.18-12.52-34-39.4-34-16,0-26,3.83-36.27,14.35L671.1,483.09c16.21-16.57,32.31-22.26,61.46-22.26,51,0,75.84,20.32,75.84,62.11V642.49H771.24V620.07l-7.44,7.44C751.4,639.91,740.11,644.3,720.64,644.3ZM730,563c-28,0-32.18,16.31-32.18,26,0,7.72,3.21,25.67,32.9,25.67,12.41,0,21.9-1.71,30.83-10.24,7.52-6.5,8.57-16.82,8.57-27V563Z" transform="translate(-309.99 -368.51)"/>
          <path fill="#ffffff" d="M841.68,711.49V463h36.8V487.7l7.6-8.45C898,466,910.27,460.83,929.8,460.83c17.33,0,32.77,5.7,42.39,15.65,16.88,17.22,18.28,45.82,18.28,76.08,0,30.46-1.4,59.23-18.26,76.43-9.64,10-25.09,15.67-42.41,15.67-19.07,0-31.7-5.17-42.23-17.28l-7.65-8.81v92.92Zm74.39-217.11c-33.35,0-36.15,30-36.15,58.18,0,28.33,2.8,58.55,36.15,58.55s36.15-30.22,36.15-58.55C952.22,524.41,949.43,494.38,916.07,494.38Z" transform="translate(-309.99 -368.51)"/>
          <path fill="#ffffff" d="M1075.6,642.49c-37,0-50.19-26.65-50.19-49.47V495.46h-19.87v-27h19.87V412.78h38.25v55.63h33.23v27h-33.23v94.67c0,13.76,7.32,21.34,20.61,21.34h12.62v31Z" transform="translate(-309.99 -368.51)"/>
          <path fill="#ffffff" d="M1193.87,644.66c-54.88,0-82.7-31-82.7-92.1,0-56.58,29.75-91.73,77.64-91.73s77.65,33.21,77.65,86.68V563H1148.69v4.36c0,28.1,17.88,46.26,45.54,46.26,20.17,0,31.12-5.61,42.58-16l22.38,21.82C1242.65,635.46,1225.85,644.66,1193.87,644.66Zm-5.06-153.53c-16.16,0-29.06,8-35.4,22.07-3.52,7.43-4.33,13-4.71,21.83l-.2,4.55h80.63l-.2-4.55c-.38-8.85-1.19-14.39-4.75-21.9C1217.88,499.17,1205,491.13,1188.81,491.13Z" transform="translate(-309.99 -368.51)"/>
          <path fill="#ffffff" d="M1341.26,644.3c-19.66,0-34.79-5.16-45-15.34-9.17-9.51-14.25-23.44-14.25-39.19,0-31.43,22.68-50.19,60.66-50.19h48.07V525.83c0-23.18-12.52-34-39.4-34-16,0-26,3.83-36.27,14.35l-23.38-23.11c16.21-16.57,32.31-22.26,61.46-22.26,51,0,75.84,20.32,75.84,62.11V642.49h-37.16V620.07l-7.44,7.44C1372,639.91,1360.73,644.3,1341.26,644.3Zm9.39-81.28c-28,0-32.18,16.31-32.18,26,0,7.72,3.21,25.67,32.9,25.67,12.41,0,21.9-1.71,30.83-10.24,7.52-6.5,8.57-16.82,8.57-27V563Z" transform="translate(-309.99 -368.51)"/>
          <path fill="#ffffff" d="M1571.76,642.49V533.06c0-17.85-9.28-38.68-35.43-38.68-16.51,0-35.79,10.13-35.79,38.68V642.49H1462.3V463h37.16v23.95l7.53-8c10.89-11.53,26.85-18.14,43.79-18.14s31.32,5.3,41.36,15.34c12.52,12.53,17.87,27.59,17.87,50.38V642.49Z" transform="translate(-309.99 -368.51)"/>

          {/* ── Yellow icon mark ── */}
          <path fill="#f8ed44" d="M619.32,594.89,484.58,374.21a11.86,11.86,0,0,0-14.08-5L608.79,595.68a12.38,12.38,0,0,1,.57,11.86L582.07,663.9a12.45,12.45,0,0,1-1.94,2.9,11.86,11.86,0,0,0,13.52-6.37l26.22-54.14A11.91,11.91,0,0,0,619.32,594.89Z" transform="translate(-309.99 -368.51)"/>
          <polygon fill="#f8ed44" points="189.82 265.75 209.48 259.48 225 285.07 189.82 265.75"/>
          <polygon fill="#f8ed44" points="98.03 81.03 145.87 158.31 133.48 186.02 35.95 215.78 98.03 81.03"/>
          <path fill="#f8ed44" d="M311,660.11l30.17-65.48,132.21-40.39,41.3,66.37L323.73,675A10.76,10.76,0,0,1,311,660.11Z" transform="translate(-309.99 -368.51)"/>
          <path fill="#f8ed44" d="M598.37,607.58l-26.32,54.33a12,12,0,0,1-20.9,1.13L412.06,440.77l29.42-64.85a12,12,0,0,1,21.09-1.29l135.24,221.5A12,12,0,0,1,598.37,607.58Z" transform="translate(-309.99 -368.51)"/>
        </svg>
      </div>

      <div style={{
        fontSize: '0.5625rem',
        color: 'var(--aptean-text-muted)',
        letterSpacing: '0.01em',
        paddingLeft: '2px',
        whiteSpace: 'nowrap',
      }}>
        Ready for What's Next, Now™
      </div>
    </div>
  )
}
